import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Droplet, Dumbbell, Timer, Award, Star, Trophy, Crown,
  Plus, Minus, Check, ChevronRight, Sparkles, Pencil, X, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { MilestoneCelebrationModal } from '../MilestoneCelebrationModal';

const MILESTONE_ICONS = {
  droplet: Droplet,
  dumbbell: Dumbbell,
  timer: Timer,
  activity: Dumbbell,
  award: Award,
  star: Star,
  trophy: Trophy,
  crown: Crown
};

export const DailyChallengeTracker = ({ challenge, participation, api, onUpdate }) => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;
  const [todayValue, setTodayValue] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [logging, setLogging] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [showMilestones, setShowMilestones] = useState(false);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetDraft, setTargetDraft] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);

  // Queue of milestone-unlock celebrations. We show them one at a time so a
  // burst of unlocks (e.g. user catches up after several days) plays as a
  // sequence instead of stacking modals.
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const currentCelebration = celebrationQueue[0] || null;

  const baseDailyTarget = challenge?.daily_target || 0;
  const unit = challenge?.unit || 'units';
  const targetValue = participation?.user_target || challenge?.target_value || 0;
  const currentValue = participation?.current_value || 0;
  const progressPct = targetValue > 0 ? Math.min(100, (currentValue / targetValue) * 100) : 0;

  useEffect(() => {
    if (challenge?.id && participation) {
      fetchMilestones();
      fetchDailyLogs();
    }
  }, [challenge?.id, participation]);

  const fetchMilestones = async () => {
    try {
      const res = await api().get(`/challenges/${challenge.id}/milestones`);
      setMilestones(res.data.milestones || []);
    } catch {
      // silent
    }
  };

  const fetchDailyLogs = async () => {
    try {
      const res = await api().get(`/challenges/${challenge.id}/daily-logs`);
      setDailyLogs(res.data.logs || []);
      
      // Find today's log
      const today = new Date().toISOString().split('T')[0];
      const todayLog = res.data.logs?.find(log => log.date === today);
      setTodayValue(todayLog?.value || 0);
    } catch {
      // silent
    }
  };

  // Admin-only: inline-edit the daily_target and persist via PUT.
  const saveTarget = async () => {
    const next = Number(targetDraft);
    if (!Number.isFinite(next) || next <= 0) {
      toast.error('Daily target must be a positive number');
      return;
    }
    if (next === baseDailyTarget) {
      setEditingTarget(false);
      return;
    }
    setSavingTarget(true);
    try {
      await api().put(`/challenges/${challenge.id}`, { daily_target: next });
      toast.success(`Daily target set to ${next}`);
      setEditingTarget(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setSavingTarget(false);
    }
  };

  const handleLogProgress = async (value) => {
    if (!value || value <= 0) return;
    
    setLogging(true);
    try {
      const res = await api().post(`/challenges/${challenge.id}/log-daily`, {
        value: parseInt(value)
      });
      
      setTodayValue(res.data.today_value);
      setInputValue('');
      
      // Check for new milestones
      if (res.data.new_milestones?.length > 0) {
        // Enrich each unlocked milestone with its description from the
        // challenge config so the modal can show "500 push-ups achieved"
        // alongside the milestone name.
        const enriched = res.data.new_milestones.map((ms) => {
          const cfg = (challenge?.milestones || []).find(
            (m) => (m.id || `milestone_${m.percentage}`) === ms.id
          );
          return { ...ms, description: cfg?.description || '' };
        });
        setCelebrationQueue((prev) => [...prev, ...enriched]);
        fetchMilestones();
      } else {
        toast.success(`Logged ${value} ${unit}!`);
      }
      
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log progress');
    }
    setLogging(false);
  };

  const quickAdd = (amount) => {
    handleLogProgress(amount);
  };

  // Quick add buttons based on challenge type
  const getQuickAddButtons = () => {
    switch (challenge?.challenge_type) {
      case 'daily_water':
        return [8, 12, 16, 24]; // oz
      case 'daily_reps':
        return [10, 25, 50, 100]; // reps
      case 'daily_duration':
        return [30, 60, 90, 120]; // seconds
      default:
        return [5, 10, 25, 50];
    }
  };

  const formatValue = (value) => {
    if (unit === 'seconds') {
      if (value >= 60) {
        const mins = Math.floor(value / 60);
        const secs = value % 60;
        return secs > 0 ? `${mins}m ${secs}s` : `${mins} min`;
      }
      return `${value}s`;
    }
    return `${value} ${unit}`;
  };

  const achievedCount = milestones.filter(ms => ms.achieved).length;
  const dailyTarget = baseDailyTarget;
  const dailyPct = dailyTarget > 0 ? Math.min(100, (todayValue / dailyTarget) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Today's Progress */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500/15 via-blue-600/5 to-transparent border border-sky-400/30 p-6
          shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_8px_28px_rgba(56,189,248,0.25)]"
      >
        {/* Decorative blue glow blob behind the card */}
        <div className="pointer-events-none absolute -top-12 -right-12 w-48 h-48 rounded-full bg-sky-500/20 blur-3xl" />

        {/* Challenge-type personality decoration (water ripple / muscle flex / timer) */}
        <ChallengeTypeDecor type={challenge?.challenge_type} />

        <div className="relative flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg text-white uppercase tracking-[0.18em]">
            Today's Progress
          </h3>
          <Badge
            className={`text-xs px-3 py-1 rounded-full ${
              dailyPct >= 100
                ? 'bg-green-500 text-white shadow-lg shadow-green-500/40'
                : 'bg-gradient-to-b from-sky-400 to-blue-700 text-white shadow-lg shadow-sky-500/40 border border-sky-300/40'
            }`}
          >
            {dailyPct >= 100 ? '✓ Daily Goal Met!' : `${Math.round(dailyPct)}%`}
          </Badge>
        </div>

        <div className="relative mb-5">
          <div className="flex justify-between text-sm mb-2.5">
            <span className="text-muted-foreground uppercase tracking-wider text-xs">Today</span>
            {editingTarget ? (
              <span className="flex items-center gap-1">
                <span className="text-sky-300 font-bold">{formatValue(todayValue)} /</span>
                <input
                  type="number"
                  autoFocus
                  value={targetDraft}
                  onChange={(e) => setTargetDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveTarget();
                    if (e.key === 'Escape') setEditingTarget(false);
                  }}
                  disabled={savingTarget}
                  className="w-16 bg-black/60 border border-sky-400/50 rounded px-1.5 py-0.5 text-sky-300 font-bold text-sm text-right focus:outline-none focus:border-sky-300"
                  data-testid="daily-target-input"
                />
                <span className="text-sky-300 font-bold">{unit}</span>
                <button
                  onClick={saveTarget}
                  disabled={savingTarget}
                  className="ml-1 text-green-400 hover:text-green-300 disabled:opacity-50"
                  aria-label="Save"
                  data-testid="daily-target-save"
                >
                  {savingTarget ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={() => setEditingTarget(false)}
                  disabled={savingTarget}
                  className="text-muted-foreground hover:text-white"
                  aria-label="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (!isAdmin) return;
                  setTargetDraft(String(baseDailyTarget || ''));
                  setEditingTarget(true);
                }}
                className={`text-sky-300 font-bold flex items-center gap-1.5 drop-shadow-[0_0_6px_rgba(56,189,248,0.5)] ${
                  isAdmin ? 'hover:text-sky-200' : 'cursor-default'
                }`}
                data-testid="daily-target-display"
                title={isAdmin ? 'Tap to edit daily target' : ''}
              >
                <span className="text-white text-base">{formatValue(todayValue)}</span>
                <span className="text-muted-foreground">/</span>
                <span>{formatValue(dailyTarget)}</span>
                {isAdmin && <Pencil className="h-3 w-3 opacity-60" />}
              </button>
            )}
          </div>
          {/* Custom progress bar with a glowing blue fill that animates in. */}
          <div className="relative h-3 rounded-full bg-black/50 border border-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(100, dailyPct)}%` }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700 shadow-[0_0_12px_rgba(56,189,248,0.7)]"
            />
          </div>
        </div>

        {/* Quick Add chips — rounded pills with hover lift + plus icon */}
        <div className="flex flex-wrap gap-2 mb-4">
          {getQuickAddButtons().map(amount => (
            <button
              key={amount}
              type="button"
              onClick={() => quickAdd(amount)}
              disabled={logging}
              className="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-white text-sm
                hover:bg-sky-500/15 hover:border-sky-400/60 hover:text-sky-300
                disabled:opacity-50 transition-all active:scale-95"
            >
              <Plus className="h-3.5 w-3.5 transition-transform group-hover:rotate-90" strokeWidth={2.5} />
              {unit === 'seconds' && amount >= 60 ? `${amount/60}m` : amount}
            </button>
          ))}
        </div>

        {/* Custom Input + LOG button */}
        <div className="relative flex gap-2">
          <Input
            type="number"
            placeholder={`Enter ${unit}…`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="bg-black/60 border-white/15 rounded-full px-4 placeholder:text-muted-foreground/60 focus-visible:ring-sky-400/60"
            min="1"
          />
          <button
            type="button"
            onClick={() => handleLogProgress(inputValue)}
            disabled={logging || !inputValue}
            className="group relative overflow-hidden inline-flex items-center justify-center gap-1.5 min-w-[90px] px-5 rounded-full text-white font-heading uppercase tracking-[0.18em] text-sm
              bg-gradient-to-b from-sky-400 to-blue-700 border border-sky-300/40
              shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_8px_24px_rgba(56,189,248,0.45)]
              hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_10px_32px_rgba(56,189,248,0.7)]
              disabled:opacity-50 disabled:cursor-not-allowed
              active:scale-[0.97] transition-all"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700" />
            {logging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={2.5} />}
            {logging ? 'Logging' : 'Log'}
          </button>
        </div>
      </motion.div>

      {/* Overall Progress — styled to match the blue "Log Progress" button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500/15 via-blue-600/5 to-transparent border border-sky-400/30 p-6
          shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_8px_28px_rgba(56,189,248,0.25)]"
      >
        {/* Decorative blue glow blob behind the card */}
        <div className="pointer-events-none absolute -top-12 -left-12 w-48 h-48 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg text-white uppercase tracking-[0.18em]">
            Challenge Progress
          </h3>
          <span className="text-sky-300 font-bold drop-shadow-[0_0_8px_rgba(56,189,248,0.6)]">
            {formatValue(currentValue)} / {formatValue(targetValue)}
          </span>
        </div>

        {/* Custom progress bar with glowing blue fill matching Log Progress button */}
        <div className="relative h-4 rounded-full bg-black/50 border border-white/5 overflow-hidden mb-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, progressPct)}%` }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700 shadow-[0_0_14px_rgba(56,189,248,0.7)]"
          >
            {/* moving highlight sheen */}
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent animate-[shimmer_2.5s_infinite]" />
          </motion.div>
        </div>

        <div className="relative flex justify-between text-sm">
          <span className="text-sky-200/80 uppercase tracking-wider text-xs">
            {Math.round(progressPct)}% complete
          </span>
          <span className="text-sky-200/80 uppercase tracking-wider text-xs">
            {participation?.days_logged || 0} days logged
          </span>
        </div>
      </motion.div>

      {/* Milestones */}
      {milestones.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border"
        >
          <button
            onClick={() => setShowMilestones(!showMilestones)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Trophy className="h-5 w-5 text-yellow-400" />
              <span className="font-heading text-white uppercase tracking-wider">
                Milestones
              </span>
              <Badge className="bg-primary/20 text-primary">
                {achievedCount} / {milestones.length}
              </Badge>
            </div>
            <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${showMilestones ? 'rotate-90' : ''}`} />
          </button>

          <AnimatePresence>
            {showMilestones && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 pt-0 space-y-3">
                  {milestones.map((milestone, idx) => {
                    const IconComponent = MILESTONE_ICONS[milestone.icon] || Award;
                    const isAchieved = milestone.achieved;
                    
                    return (
                      <motion.div
                        key={milestone.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className={`flex items-center gap-4 p-3 border ${
                          isAchieved 
                            ? 'border-primary/50 bg-primary/10' 
                            : 'border-border bg-black/30'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isAchieved 
                            ? 'bg-primary text-black' 
                            : 'bg-white/10 text-muted-foreground'
                        }`}>
                          {isAchieved ? (
                            <Check className="h-6 w-6" />
                          ) : (
                            <IconComponent className="h-6 w-6" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${isAchieved ? 'text-primary' : 'text-white'}`}>
                              {milestone.name}
                            </span>
                            <Badge className="bg-white/10 text-muted-foreground text-xs">
                              {milestone.percentage}%
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {milestone.description}
                          </p>
                        </div>

                        {isAchieved && (
                          <Sparkles className="h-5 w-5 text-yellow-400" />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Recent Logs */}
      {dailyLogs.length > 0 && (
        <div className="bg-card border border-border p-4">
          <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">
            Recent Activity
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {dailyLogs.slice(0, 7).map((log, idx) => (
              <div key={log.id || idx} className="flex items-center justify-between p-2 bg-black/30">
                <span className="text-muted-foreground text-sm">
                  {new Date(log.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
                <span className="text-white font-medium">{formatValue(log.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <MilestoneCelebrationModal
        isOpen={!!currentCelebration}
        milestone={currentCelebration}
        onClose={() => setCelebrationQueue((prev) => prev.slice(1))}
      />
    </div>
  );
};

/**
 * Challenge-type personality decoration rendered behind the
 * "Today's Progress" header. Adds a subtle animated motif keyed off
 * the challenge type — ripples for water, a flexing dumbbell for reps,
 * a rotating timer for duration challenges.
 */
const ChallengeTypeDecor = ({ type }) => {
  if (type === 'daily_water') {
    return (
      <div className="pointer-events-none absolute top-4 right-20 w-20 h-20" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="water-ripple absolute inset-0 rounded-full border border-sky-300/60"
            style={{ animationDelay: `${i * 1.05}s` }}
          />
        ))}
        <Droplet
          className="absolute inset-0 m-auto h-5 w-5 text-sky-300/80 drop-shadow-[0_0_8px_rgba(56,189,248,0.7)]"
          strokeWidth={2}
        />
      </div>
    );
  }
  if (type === 'daily_reps') {
    return (
      <div className="pointer-events-none absolute top-5 right-24 w-14 h-14" aria-hidden="true">
        <Dumbbell
          className="muscle-flex h-10 w-10 text-sky-300/85"
          strokeWidth={2.2}
        />
      </div>
    );
  }
  if (type === 'daily_duration') {
    return (
      <div className="pointer-events-none absolute top-5 right-24 w-14 h-14 timer-pulse" aria-hidden="true">
        <Timer
          className="timer-tick h-10 w-10 text-sky-300/85"
          strokeWidth={2.2}
        />
      </div>
    );
  }
  return null;
};

export const MilestonesBadge = ({ milestones, achieved }) => {
  const achievedCount = achieved?.length || 0;
  const totalCount = milestones?.length || 0;
  
  if (totalCount === 0) return null;
  
  return (
    <Badge className="bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
      <Trophy className="h-3 w-3" />
      {achievedCount}/{totalCount}
    </Badge>
  );
};
